import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Trophy, BarChart3, CalendarClock, Home, ShieldCheck, Menu, X, Ticket, User } from "lucide-react";
import { useState, useEffect } from "react";
import NotificationBell from "@/components/NotificationBell";
import Banner from "@/components/Banner";
import AnnouncementModal from "@/components/AnnouncementModal";

const ANT_LOGO = "/ant-emblem.png";

const navItems = [
  { to: "/", label: "Pronostics", icon: Home, testid: "nav-dashboard" },
  { to: "/resultats", label: "Résultats", icon: CalendarClock, testid: "nav-results" },
  { to: "/statistiques", label: "Statistiques", icon: BarChart3, testid: "nav-stats" },
  { to: "/jeu", label: "Jeu", icon: Ticket, testid: "nav-game" },
  { to: "/challenge", label: "Classement", icon: Trophy, testid: "nav-challenge" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const clockLabel = now.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" }) +
    " · " + now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const items = [...navItems];
  const isStaff = user?.role === "admin" || user?.is_official || user?.is_moderator;
  if (isStaff) {
    const label = user?.role === "admin" ? "Espace Admin" : user?.is_official ? "Mes pronostics" : "Modération";
    items.push({ to: "/admin", label, icon: ShieldCheck, testid: "nav-admin" });
  }

  const roleLabel = user?.role === "admin" ? "Administrateur" : user?.is_official ? "Membre officiel" : user?.is_moderator ? "Modérateur" : "Membre";

  const linkClass = (to) =>
    `flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
      location.pathname === to
        ? "bg-[#00E676] text-[#04180a]"
        : "text-[#A1B5A8] hover:text-white hover:bg-[#1A2B20]"
    }`;

  return (
    <div className="min-h-screen ant-radial">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0B120E]/80 border-b border-[#00E676]/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
            <img src={ANT_LOGO} alt="ANT Pronostics" className="h-11 w-auto object-contain drop-shadow-[0_0_10px_rgba(0,230,118,0.35)]" />
            <div className="leading-tight">
              <div className="font-script text-2xl text-[#39FF14]">A Nous Turf</div>
              <div className="text-[10px] tabular text-[#697C6F] capitalize" data-testid="brand-clock">{clockLabel}</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {items.map((it) => (
              <Link key={it.to} to={it.to} className={linkClass(it.to)} data-testid={it.testid}>
                <it.icon size={16} />
                {it.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <NotificationBell />
            <Link to="/profil" data-testid="nav-profile" className="flex items-center gap-2 group">
              {user?.avatar ? (
                <img src={user.avatar} alt="" className="h-9 w-9 rounded-full object-cover ring-2 ring-[#00E676]/40" />
              ) : (
                <span className="h-9 w-9 rounded-full grid place-items-center bg-[#1A2B20] text-[#39FF14] font-bold ring-2 ring-[#00E676]/30">
                  {(user?.prenom?.[0] || "") + (user?.nom?.[0] || "")}
                </span>
              )}
              <div className="text-right leading-tight">
                <div className="text-sm font-semibold text-white group-hover:text-[#39FF14] transition-colors">{user?.prenom} {user?.nom}</div>
                <div className="text-[11px] text-[#697C6F]">{roleLabel}</div>
              </div>
            </Link>
            <button onClick={doLogout} data-testid="logout-btn" className="p-2 rounded-lg text-[#A1B5A8] hover:text-white hover:bg-[#1A2B20] transition-colors">
              <LogOut size={18} />
            </button>
          </div>

          <button className="lg:hidden p-2 text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X /> : <Menu />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-[#00E676]/15 px-4 py-3 space-y-1 bg-[#0B120E]">
            {items.map((it) => (
              <Link key={it.to} to={it.to} className={linkClass(it.to)} onClick={() => setOpen(false)}>
                <it.icon size={16} />
                {it.label}
              </Link>
            ))}
            <Link to="/profil" className={linkClass("/profil")} onClick={() => setOpen(false)}>
              <User size={16} /> Mon profil
            </Link>
            <button onClick={doLogout} className="flex w-full items-center gap-2 px-4 py-2 rounded-lg text-sm text-[#A1B5A8] hover:bg-[#1A2B20]">
              <LogOut size={16} /> Déconnexion
            </button>
          </div>
        )}
      </header>

      <Banner />
      <AnnouncementModal />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">{children}</main>

      <footer className="border-t border-[#00E676]/10 mt-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#697C6F]">
          <div>© {now.getFullYear()} À Nous Turf · Service de pronostics & divertissement</div>
          <div className="flex items-center gap-4">
            <Link to="/politique-confidentialite" data-testid="footer-privacy" className="hover:text-[#39FF14] transition-colors">Politique de confidentialité</Link>
            <span className="text-[#2d3b32]">·</span>
            <Link to="/mentions-legales" data-testid="footer-legal" className="hover:text-[#39FF14] transition-colors">Mentions légales / CGU</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
